/**
 * useAudioClip - Personal Context Clip Recording Hook
 *
 * Records ONLY the user's local microphone. Never captures meeting audio.
 * Features:
 * - Rolling 30s buffer (captures context before clip starts)
 * - Forward recording up to 30s
 * - Auto-mute meeting mic on start
 * - Early stop support
 * - Silent failure handling
 * - Clips <1s are discarded
 *
 * @see DESIGN_SYSTEM.md for UI guidelines
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

// Constants
const BUFFER_DURATION_MS = 30000; // 30 seconds
const MAX_FORWARD_DURATION_MS = 30000; // 30 seconds
const MAX_TOTAL_DURATION_MS = 60000; // 60 seconds total
const MIN_CLIP_DURATION_MS = 1000; // Discard clips under 1 second
const CHUNK_INTERVAL_MS = 1000; // Collect data every 1 second
const AUDIO_BITRATE = 128000; // 128kbps

export const useAudioClip = (userId) => {
  // State
  const [isBuffering, setIsBuffering] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasPermission, setHasPermission] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Refs
  const bufferStreamRef = useRef(null);
  const bufferRecorderRef = useRef(null);
  const rollingChunksRef = useRef([]);
  const forwardRecorderRef = useRef(null);
  const forwardChunksRef = useRef([]);
  const recordingStartTimeRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const bufferStartTimeRef = useRef(null);
  const isRecordingRef = useRef(false); // Ref for closure-safe check
  const recordingTimeRef = useRef(0); // Ref for closure-safe time

  /**
   * Check if mic permission is available
   * Called on mount to determine if feature should be visible
   */
  const checkPermission = useCallback(async () => {
    try {
      // Check permission state without prompting
      const result = await navigator.permissions.query({ name: 'microphone' });
      setHasPermission(result.state === 'granted');

      // Listen for permission changes
      result.onchange = () => {
        setHasPermission(result.state === 'granted');
      };
    } catch {
      // Permissions API not supported, assume we need to request
      setHasPermission(null);
    }
  }, []);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  /**
   * Get a fresh mic stream - ONLY local microphone, never meeting audio
   * This is the critical security boundary
   */
  const getMicStream = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false // Never capture video
      });
      setHasPermission(true);
      return stream;
    } catch (error) {
      console.warn('Mic access denied for context clip:', error.name);
      setHasPermission(false);
      return null;
    }
  }, []);

  /**
   * Start the rolling buffer
   * Called when user joins a meeting - runs continuously in background
   */
  const startBuffer = useCallback(async () => {
    if (isBuffering || isRecording) return false;

    const stream = await getMicStream();
    if (!stream) return false;

    try {
      // Determine supported audio format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4'; // Safari fallback

      bufferStreamRef.current = stream;
      rollingChunksRef.current = [];
      bufferStartTimeRef.current = Date.now();

      const recorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: AUDIO_BITRATE
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          const now = Date.now();
          rollingChunksRef.current.push({
            data: event.data,
            timestamp: now
          });

          // Remove chunks older than buffer duration
          const cutoff = now - BUFFER_DURATION_MS;
          rollingChunksRef.current = rollingChunksRef.current.filter(
            chunk => chunk.timestamp > cutoff
          );
        }
      };

      recorder.start(CHUNK_INTERVAL_MS);
      bufferRecorderRef.current = recorder;
      setIsBuffering(true);
      return true;
    } catch (error) {
      console.error('[AudioClip] Failed to start buffer:', error);
      stream.getTracks().forEach(t => t.stop());
      return false;
    }
  }, [isBuffering, isRecording, getMicStream]);

  /**
   * Stop the rolling buffer
   * Called when user leaves meeting
   */
  const stopBuffer = useCallback(() => {
    if (bufferRecorderRef.current && bufferRecorderRef.current.state !== 'inactive') {
      bufferRecorderRef.current.stop();
    }
    if (bufferStreamRef.current) {
      bufferStreamRef.current.getTracks().forEach(t => t.stop());
      bufferStreamRef.current = null;
    }
    bufferRecorderRef.current = null;
    rollingChunksRef.current = [];
    setIsBuffering(false);
  }, []);

  /**
   * Start recording a clip
   * Freezes the current buffer and begins forward recording
   *
   * @param {Function} onMeetingMute - Callback to mute meeting mic
   * @returns {boolean} Success
   */
  const startClipRecording = useCallback(async (onMeetingMute) => {
    if (isRecording) return false;

    // Get a fresh stream for forward recording (buffer uses its own)
    let forwardStream = bufferStreamRef.current;

    if (!forwardStream) {
      // Buffer wasn't running, start fresh
      forwardStream = await getMicStream();
      if (!forwardStream) return false;
    }

    try {
      // Freeze the current buffer chunks
      const frozenBuffer = [...rollingChunksRef.current];

      // Mute meeting mic (UX requirement)
      if (onMeetingMute) {
        onMeetingMute(true);
      }

      // Determine supported audio format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      // Start forward recording
      forwardChunksRef.current = [];
      const recorder = new MediaRecorder(forwardStream, {
        mimeType,
        audioBitsPerSecond: AUDIO_BITRATE
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          forwardChunksRef.current.push(event.data);
        }
      };

      recorder.start(CHUNK_INTERVAL_MS);
      forwardRecorderRef.current = recorder;
      recordingStartTimeRef.current = Date.now();

      // Store frozen buffer for later combination
      forwardRecorderRef.current._frozenBuffer = frozenBuffer;
      forwardRecorderRef.current._mimeType = mimeType;

      setIsRecording(true);
      isRecordingRef.current = true;
      setRecordingTime(0);
      recordingTimeRef.current = 0;

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - recordingStartTimeRef.current;
        const seconds = Math.floor(elapsed / 1000);
        setRecordingTime(seconds);
        recordingTimeRef.current = seconds;

        // Auto-stop at max forward duration
        if (elapsed >= MAX_FORWARD_DURATION_MS) {
          stopClipRecording();
        }
      }, 100); // Update frequently for smooth countdown

      console.log('[AudioClip] Clip recording started');
      return true;
    } catch (error) {
      console.error('[AudioClip] Failed to start clip recording:', error);
      return false;
    }
  }, [isRecording, getMicStream]);

  /**
   * Stop recording and return the combined clip blob
   * @returns {Promise<{blob: Blob, duration: number} | null>}
   */
  const stopClipRecording = useCallback(() => {
    return new Promise((resolve) => {
      // Use ref for closure-safe check
      if (!forwardRecorderRef.current || !isRecordingRef.current) {
        console.log('[AudioClip] Stop called but not recording');
        resolve(null);
        return;
      }

      console.log('[AudioClip] Stopping clip recording...');

      // Clear timer first
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      const recorder = forwardRecorderRef.current;
      const frozenBuffer = recorder._frozenBuffer || [];
      const mimeType = recorder._mimeType || 'audio/webm';
      const currentRecordingTime = recordingTimeRef.current;

      // Mark as not recording immediately to prevent double-stops
      isRecordingRef.current = false;

      recorder.onstop = () => {
        console.log('[AudioClip] Recorder onstop fired');
        setIsRecording(false);

        // Combine frozen buffer + forward chunks
        const allChunks = [
          ...frozenBuffer.map(c => c.data),
          ...forwardChunksRef.current
        ];

        if (allChunks.length === 0) {
          console.log('[AudioClip] No audio data captured');
          resolve(null);
          return;
        }

        const blob = new Blob(allChunks, { type: mimeType });
        const duration = currentRecordingTime * 1000; // Convert to ms

        // Discard clips under 1 second
        if (duration < MIN_CLIP_DURATION_MS) {
          console.log('[AudioClip] Clip too short, discarding');
          resolve(null);
          return;
        }

        // Calculate actual duration including buffer
        const bufferDuration = frozenBuffer.length > 0
          ? Math.min(BUFFER_DURATION_MS, frozenBuffer[frozenBuffer.length - 1]?.timestamp - frozenBuffer[0]?.timestamp || 0)
          : 0;
        const totalDuration = bufferDuration + (currentRecordingTime * 1000);

        console.log('[AudioClip] Clip created:', {
          bufferChunks: frozenBuffer.length,
          forwardChunks: forwardChunksRef.current.length,
          sizeKB: Math.round(blob.size / 1024),
          durationSeconds: Math.round(totalDuration / 1000)
        });

        resolve({
          blob,
          duration: Math.round(totalDuration / 1000), // seconds
          mimeType
        });

        // Reset
        forwardChunksRef.current = [];
        setRecordingTime(0);
        recordingTimeRef.current = 0;
      };

      if (recorder.state !== 'inactive') {
        console.log('[AudioClip] Requesting final data and stopping recorder');
        recorder.requestData();
        setTimeout(() => {
          if (recorder.state !== 'inactive') {
            recorder.stop();
          }
        }, 100);
      } else {
        console.log('[AudioClip] Recorder already inactive, calling onstop manually');
        recorder.onstop();
      }
    });
  }, []); // No dependencies - uses refs for all values

  /**
   * Upload clip to Firebase Storage
   * Fails silently - returns null on error
   *
   * @param {Blob} blob - Audio blob
   * @param {string} meetingId - Meeting ID
   * @param {string} noteId - Note ID to associate with
   * @returns {Promise<{url: string, duration: number, size: number} | null>}
   */
  const uploadClip = useCallback(async (blob, meetingId, noteId, duration) => {
    if (!blob || !userId) return null;

    setIsUploading(true);

    try {
      const clipId = `${meetingId}_${noteId}_${Date.now()}`;
      const extension = blob.type.includes('mp4') ? 'mp4' : 'webm';
      const storagePath = `clips/${userId}/${clipId}.${extension}`;

      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, blob);

      return new Promise((resolve) => {
        uploadTask.on(
          'state_changed',
          null, // Don't track progress - keep it silent
          (error) => {
            // Silent failure - log but don't surface to user
            console.warn('[AudioClip] Upload failed silently:', error.code);
            setIsUploading(false);
            resolve(null);
          },
          async () => {
            try {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

              setIsUploading(false);

              console.log('[AudioClip] Upload complete:', {
                clipId,
                sizeKB: Math.round(blob.size / 1024)
              });

              resolve({
                url: downloadUrl,
                duration,
                size: blob.size,
                storagePath
              });
            } catch {
              setIsUploading(false);
              resolve(null);
            }
          }
        );
      });
    } catch {
      setIsUploading(false);
      return null;
    }
  }, [userId]);

  /**
   * Get the maximum recording time remaining
   */
  const getMaxRemainingTime = useCallback(() => {
    return Math.max(0, Math.floor((MAX_FORWARD_DURATION_MS / 1000) - recordingTime));
  }, [recordingTime]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopBuffer();
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [stopBuffer]);

  return {
    // State
    isBuffering,
    isRecording,
    recordingTime,
    hasPermission,
    isUploading,

    // Actions
    startBuffer,
    stopBuffer,
    startClipRecording,
    stopClipRecording,
    uploadClip,
    checkPermission,

    // Helpers
    getMaxRemainingTime,
    maxDuration: MAX_FORWARD_DURATION_MS / 1000, // 30 seconds
    maxTotalDuration: MAX_TOTAL_DURATION_MS / 1000 // 60 seconds
  };
};

export default useAudioClip;
